import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/Card';
import { Layout, Facebook, Instagram, ShieldCheck, Search, Loader2 } from 'lucide-react';

export default function MetaPagesLab() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (selectedBrand?.id) {
      fetchPages();
    }
  }, [selectedBrand?.id]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch('https://api.glowytics.com/api/meta-ads/pages', {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setPages(data.pages);
      } else {
        setError(data.error || 'Failed to fetch pages');
      }
    } catch (e) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const filteredPages = pages.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.id.includes(searchTerm)
  );

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Facebook className="w-8 h-8 text-blue-600" />
            Meta Pages Explorer
          </h1>
          <p className="text-gray-500 mt-2 text-lg">
            List of Facebook Pages and Instagram accounts accessible via your stored tokens.
          </p>
        </div>
        <button 
          onClick={fetchPages}
          disabled={loading}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh List'}
        </button>
      </div>

      <Card className="mb-6 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
          <input 
            type="text"
            placeholder="Search pages by name or ID..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </Card>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-xl">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-500">Fetching pages from Meta...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredPages.length > 0 ? (
            filteredPages.map(page => (
              <div key={page.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 flex-shrink-0">
                      <Facebook className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{page.name}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs font-mono text-gray-400">ID: {page.id}</span>
                        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {page.category}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 min-w-[300px]">
                    <div className="flex items-center gap-2 text-sm">
                      <ShieldCheck className="w-4 h-4 text-emerald-500" />
                      <span className="text-gray-600 truncate max-w-[250px]" title={page.access_token}>
                        Token: {page.access_token.substring(0, 15)}...
                      </span>
                    </div>
                    
                    <div className="border-t border-dashed border-gray-100 pt-2">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Instagram className="w-4 h-4 text-pink-500" />
                        Instagram Identities
                      </div>
                      
                      {page.instagram_business_account ? (
                        <div className="bg-pink-50 text-pink-700 px-3 py-2 rounded-lg text-xs flex items-center justify-between">
                          <span className="font-medium">Business Account:</span>
                          <span className="font-mono">{page.instagram_business_account.id || page.instagram_business_account}</span>
                        </div>
                      ) : null}

                      {page.instagram_accounts?.data?.length > 0 ? (
                        <div className="mt-2 space-y-1">
                          {page.instagram_accounts.data.map(ig => (
                            <div key={ig.id} className="bg-gray-50 text-gray-600 px-3 py-2 rounded-lg text-xs flex items-center justify-between">
                              <span>@{ig.username || 'unknown'}</span>
                              <span className="font-mono text-[10px]">{ig.id}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        !page.instagram_business_account && (
                          <div className="text-[10px] text-gray-400 italic">No linked Instagram accounts found.</div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
              <p className="text-gray-500">No pages match your search.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

